import React from "react";
import { getDisplayUnitId } from "../../../utils/unitDisplayId";
import {
  getRoomOccupancyStatus,
  isActiveRoom,
} from "../../../utils/unitOccupancyStatus";
import {
  coerceRevenueTypeForFrequency,
  monthlyEquivalentFromRevenueRows,
  normalizeRevenueFrequency,
  revenueFrequencyLabel,
  revenueTypeLabelForDisplay,
} from "../../../utils/tenancyRevenueBreakdown";
import {
  applyRecurringRevenueDatesFromTenancy,
  assignRoomOptionTitleDe,
  assignRoomStatusSuffixDe,
  dateOnlyOrNull,
  deriveTenancyLifecyclePreviewForAssign,
  formatChfRent,
  initialAssignSecondForm,
  parseRevenueAmount,
  roomOverlapsAssignProposal,
  tenancyDisplayStatusLabelDe,
  tenancyDraftDisplayEndIso,
} from "./tenantDetailHelpers";
import { formatDateOnly } from "../../../utils/datetime";
import {
  gridTwoCol,
  inputClass,
  labelClass,
  tableClass,
  tdCellClass,
  thCellClass,
} from "./tenantDetailStyles";
import RevenueTypeSelect from "./RevenueTypeSelect";
import TenantParticipantPicker from "./TenantParticipantPicker";

export default function TenancyAssignForm({
  assignUnitsErr,
  assignErr,
  assignUnitId,
  setAssignUnitId,
  assignRoomId,
  setAssignRoomId,
  assignUnits,
  assignUnitsLoading,
  assignRooms,
  assignRoomsLoading,
  assignUnitTenancies,
  assignMoveIn,
  setAssignMoveIn,
  assignMoveOut,
  setAssignMoveOut,
  assignCancellationOpen,
  setAssignCancellationOpen,
  assignTerminationEffective,
  setAssignTerminationEffective,
  terminationMaxIso,
  assignActualMoveOut,
  setAssignActualMoveOut,
  assignNoticeGivenAt,
  setAssignNoticeGivenAt,
  assignTerminatedBy,
  setAssignTerminatedBy,
  assignMinimumDurationMonths,
  setAssignMinimumDurationMonths,
  assignNoticePeriodMonths,
  setAssignNoticePeriodMonths,
  assignTenantDepositType,
  setAssignTenantDepositType,
  assignTenantDepositAmount,
  setAssignTenantDepositAmount,
  assignTenantDepositProvider,
  setAssignTenantDepositProvider,
  assignSecondInlineOpen,
  setAssignSecondInlineOpen,
  assignSecondRole,
  setAssignSecondRole,
  assignSecondForm,
  setAssignSecondForm,
  assignSecondMode,
  setAssignSecondMode,
  assignSecondSelectedTenant,
  onSelectExistingSecond,
  onClearExistingSecond,
  assignSecondSearchQuery,
  setAssignSecondSearchQuery,
  assignSecondSearchResults,
  assignSecondSearchLoading,
  assignSecondSearchErr,
  assignRevenueRows,
  setAssignRevenueRows,
  assignRevenueForm,
  setAssignRevenueForm,
  assignSaving,
  onSubmit,
  setAssignOpen,
  resetAssignForm,
}) {
  return (
                  <form
                    onSubmit={onSubmit}
                    className="mt-3.5 border-t border-black/10 pt-3.5 dark:border-white/[0.05]"
                  >
                    {assignUnitsErr ? (
                      <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#f87171" }}>
                        {assignUnitsErr}
                      </p>
                    ) : null}
                    {assignErr ? (
                      <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#f87171" }}>
                        {assignErr}
                      </p>
                    ) : null}
                    <div style={gridTwoCol}>
                      <div>
                        <label htmlFor="assign-unit" className={labelClass}>
                          Einheit *
                        </label>
                        <select
                          id="assign-unit"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignUnitId}
                          onChange={(e) => {
                            setAssignUnitId(e.target.value);
                            setAssignRoomId("");
                          }}
                          disabled={assignSaving || assignUnitsLoading}
                        >
                          <option value="">
                            {assignUnitsLoading ? "Lade Einheiten …" : "— Einheit wählen"}
                          </option>
                          {assignUnits.map((u) => {
                            const loc = String(u.address || u.place || "").trim();
                            const label = loc
                              ? `${getDisplayUnitId(u)} — ${loc}`
                              : getDisplayUnitId(u);
                            return (
                              <option key={String(u.id)} value={String(u.id)}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="assign-room" className={labelClass}>
                          Zimmer *
                        </label>
                        <select
                          id="assign-room"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignRoomId}
                          onChange={(e) => {
                            setAssignRoomId(e.target.value);
                          }}
                          disabled={assignSaving || !assignUnitId || assignRoomsLoading}
                        >
                          <option value="">
                            {!assignUnitId
                              ? "— Zuerst Einheit wählen"
                              : assignRoomsLoading
                                ? "Lade Zimmer …"
                                : "— Zimmer wählen"}
                          </option>
                          {assignRooms.map((r) => {
                            const base = r.roomName || r.name || r.room_number || r.id;
                            const occ = getRoomOccupancyStatus(r, assignUnitTenancies) || "frei";
                            const overlaps = roomOverlapsAssignProposal(
                              r,
                              assignUnitTenancies,
                              assignMoveIn,
                              assignMoveOut
                            );
                            // Date-aware only (Slice A, inclusive end dates): there is
                            // NO same-day handover — a currently belegt/reserviert room
                            // becomes selectable only on move_out_date + 1 (the first
                            // free day after the existing tenancy's effective end).
                            // roomOverlapsAssignProposal is a UX-only precheck; the
                            // backend/DB remains the source of truth. The current occ
                            // still drives the informational suffix below.
                            // A STOOD-DOWN ROOM OUTRANKS BOTH ANSWERS BELOW.
                            // `getRoomOccupancyStatus` has no branch for one, and
                            // a retired room carries no tenancy, so it arrived here
                            // as "frei" and overlapped with nothing — the dropdown
                            // offered it as available and let it be chosen. The
                            // backend now refuses that create outright; this is the
                            // affordance in front of that rule.
                            //
                            // `isActiveRoom`, never `room.status`: the wire sends
                            // "Frei" in that legacy column even for a retired room,
                            // so it would get this exact case wrong.
                            const active = isActiveRoom(r);
                            const disabled = !active || overlaps;
                            let suffix = assignRoomStatusSuffixDe(occ);
                            if (overlaps) suffix = "nicht verfügbar";
                            // Last, so it wins: a room that cannot be let at all is
                            // not "nicht verfügbar for these dates".
                            if (!active) suffix = "Inaktiv";
                            const label = `${base} — ${suffix}`;
                            // Set here for the same reason the suffix above is: the
                            // shared helper answers the occupancy question, and this
                            // is not one. Its signature stays untouched.
                            const title = !active
                              ? "Inaktives Zimmer — nicht vermietbar"
                              : assignRoomOptionTitleDe(occ, disabled, overlaps);
                            return (
                              <option
                                key={String(r.id)}
                                value={String(r.id)}
                                disabled={disabled}
                                title={title}
                              >
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="assign-move-in" className={labelClass}>
                          Einzugsdatum *
                        </label>
                        <input
                          id="assign-move-in"
                          type="date"
                          className={inputClass}
                          value={assignMoveIn}
                          onChange={(e) => setAssignMoveIn(e.target.value)}
                          disabled={assignSaving}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="assign-move-out" className={labelClass}>
                          Befristet bis / Vertragsende
                        </label>
                        <input
                          id="assign-move-out"
                          type="date"
                          className={inputClass}
                          value={assignMoveOut}
                          onChange={(e) => setAssignMoveOut(e.target.value)}
                          disabled={assignSaving}
                        />
                        <div className="mt-0.5 text-[10px] text-[#7f8daa]">
                          Vertragliches Enddatum eines befristeten Mietverhältnisses. Keine Kündigung.
                        </div>
                      </div>
                      <div>
                        <label htmlFor="assign-min-duration" className={labelClass}>
                          Mindestmietdauer
                        </label>
                        <select
                          id="assign-min-duration"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignMinimumDurationMonths}
                          onChange={(e) => setAssignMinimumDurationMonths(e.target.value)}
                          disabled={assignSaving}
                        >
                          <option value="0">Keine Mindestdauer</option>
                          <option value="1">1 Monat</option>
                          <option value="2">2 Monate</option>
                          <option value="3">3 Monate</option>
                          <option value="6">6 Monate</option>
                          <option value="12">12 Monate</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="assign-notice-period" className={labelClass}>
                          Kündigungsfrist
                        </label>
                        <select
                          id="assign-notice-period"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignNoticePeriodMonths}
                          onChange={(e) => setAssignNoticePeriodMonths(e.target.value)}
                          disabled={assignSaving}
                        >
                          <option value="0">Sofortige Kündigung</option>
                          <option value="1">1 Monat</option>
                          <option value="2">2 Monate</option>
                          <option value="3">3 Monate</option>
                        </select>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <button
                          type="button"
                          onClick={() => setAssignCancellationOpen((v) => !v)}
                          disabled={assignSaving}
                          className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                        >
                          {assignCancellationOpen
                            ? "Kündigung / Austritt ausblenden"
                            : "Kündigung / Austritt erfassen"}
                        </button>
                      </div>
                      {assignCancellationOpen ? (
                        <>
                      <div>
                        <label htmlFor="assign-notice" className={labelClass}>
                          Kündigung eingegangen am
                        </label>
                        <input
                          id="assign-notice"
                          type="date"
                          className={inputClass}
                          value={assignNoticeGivenAt}
                          onChange={(e) => setAssignNoticeGivenAt(e.target.value)}
                          disabled={assignSaving}
                        />
                      </div>
                      <div>
                        <label htmlFor="assign-te" className={labelClass}>
                          Kündigung wirksam per
                        </label>
                        <input
                          id="assign-te"
                          type="date"
                          className={inputClass}
                          value={assignTerminationEffective}
                          onChange={(e) => setAssignTerminationEffective(e.target.value)}
                          max={terminationMaxIso || undefined}
                          disabled={assignSaving}
                        />
                        {terminationMaxIso ? (
                          <div className="mt-0.5 text-[10px] text-[#7f8daa]">
                            Maximal bis {formatDateOnly(terminationMaxIso)} gemäss Vermieter-Vertrag
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <label htmlFor="assign-am" className={labelClass}>
                          Rückgabe erfolgt am
                        </label>
                        <input
                          id="assign-am"
                          type="date"
                          className={inputClass}
                          value={assignActualMoveOut}
                          onChange={(e) => setAssignActualMoveOut(e.target.value)}
                          disabled={assignSaving}
                        />
                      </div>
                      <div>
                        <label htmlFor="assign-tb" className={labelClass}>
                          Gekündigt durch
                        </label>
                        <select
                          id="assign-tb"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignTerminatedBy}
                          onChange={(e) => setAssignTerminatedBy(e.target.value)}
                          disabled={assignSaving}
                        >
                          <option value="">—</option>
                          <option value="tenant">Mieter</option>
                          <option value="landlord">Vermieter / Verwaltung</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </div>
                        </>
                      ) : null}
                      <div style={{ gridColumn: "1 / -1" }}>
                        {!assignSecondInlineOpen ? (
                          <button
                            type="button"
                            onClick={() => {
                              setAssignSecondInlineOpen(true);
                              setAssignSecondRole("");
                            }}
                            disabled={assignSaving}
                            className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                            style={{ cursor: assignSaving ? "default" : "pointer" }}
                          >
                            Zweitmieter hinzufügen
                          </button>
                        ) : (
                          <div className="rounded-[10px] border border-black/10 bg-slate-50 p-3 dark:border-white/[0.07] dark:bg-[#111520]">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
                                Zweitperson (optional)
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setAssignSecondInlineOpen(false);
                                  setAssignSecondForm(initialAssignSecondForm());
                                  setAssignSecondRole("");
                                  if (onClearExistingSecond) onClearExistingSecond();
                                }}
                                disabled={assignSaving}
                                className="text-[12px] font-semibold text-[#64748b] underline dark:text-[#8090b0]"
                                style={{ cursor: assignSaving ? "default" : "pointer" }}
                              >
                                Entfernen
                              </button>
                            </div>
                            <div className="mb-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setAssignSecondMode("existing")}
                                disabled={assignSaving}
                                aria-pressed={assignSecondMode === "existing"}
                                className={`rounded-[8px] border px-3 py-1.5 text-[12px] font-semibold ${
                                  assignSecondMode === "existing"
                                    ? "border-[#5b8cff] bg-[#5b8cff]/10 text-[#5b8cff]"
                                    : "border-black/10 bg-transparent text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
                                }`}
                                style={{ cursor: assignSaving ? "default" : "pointer" }}
                              >
                                Bestehenden Mieter auswählen
                              </button>
                              <button
                                type="button"
                                onClick={() => setAssignSecondMode("new")}
                                disabled={assignSaving}
                                aria-pressed={assignSecondMode === "new"}
                                className={`rounded-[8px] border px-3 py-1.5 text-[12px] font-semibold ${
                                  assignSecondMode === "new"
                                    ? "border-[#5b8cff] bg-[#5b8cff]/10 text-[#5b8cff]"
                                    : "border-black/10 bg-transparent text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
                                }`}
                                style={{ cursor: assignSaving ? "default" : "pointer" }}
                              >
                                Neuen Mieter erfassen
                              </button>
                            </div>
                            <div style={gridTwoCol}>
                              <div>
                                <label htmlFor="assign-second-role-inline" className={labelClass}>
                                  Rolle *
                                </label>
                                <select
                                  id="assign-second-role-inline"
                                  className={inputClass}
                                  style={{ cursor: assignSaving ? "default" : "pointer" }}
                                  value={assignSecondRole}
                                  onChange={(e) => setAssignSecondRole(e.target.value)}
                                  disabled={assignSaving}
                                >
                                  <option value="">— wählen</option>
                                  <option value="co_tenant">Zweitmieter (Co-Mieter)</option>
                                  <option value="solidarhafter">Solidarhafter</option>
                                </select>
                              </div>
                              {assignSecondMode === "existing" ? (
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <TenantParticipantPicker
                                    query={assignSecondSearchQuery}
                                    onQueryChange={setAssignSecondSearchQuery}
                                    results={assignSecondSearchResults}
                                    loading={assignSecondSearchLoading}
                                    error={assignSecondSearchErr}
                                    selectedTenant={assignSecondSelectedTenant}
                                    onSelect={onSelectExistingSecond}
                                    onClear={onClearExistingSecond}
                                    disabled={assignSaving}
                                  />
                                </div>
                              ) : null}
                              {assignSecondMode === "new" ? (
                              <React.Fragment>
                              <div>
                                <label htmlFor="assign-s2-fn" className={labelClass}>
                                  Vorname *
                                </label>
                                <input
                                  id="assign-s2-fn"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.firstName}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, firstName: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                  autoComplete="given-name"
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-ln" className={labelClass}>
                                  Nachname *
                                </label>
                                <input
                                  id="assign-s2-ln"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.lastName}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, lastName: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                  autoComplete="family-name"
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-email" className={labelClass}>
                                  E-Mail
                                </label>
                                <input
                                  id="assign-s2-email"
                                  type="email"
                                  className={inputClass}
                                  value={assignSecondForm.email}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, email: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                  autoComplete="email"
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-phone" className={labelClass}>
                                  Telefon
                                </label>
                                <input
                                  id="assign-s2-phone"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.phone}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, phone: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                  autoComplete="tel"
                                />
                              </div>
                              <div style={{ gridColumn: "1 / -1" }}>
                                <label htmlFor="assign-s2-street" className={labelClass}>
                                  Strasse
                                </label>
                                <input
                                  id="assign-s2-street"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.street}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, street: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-plz" className={labelClass}>
                                  PLZ
                                </label>
                                <input
                                  id="assign-s2-plz"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.postalCode}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, postalCode: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-city" className={labelClass}>
                                  Ort
                                </label>
                                <input
                                  id="assign-s2-city"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.city}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, city: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-country" className={labelClass}>
                                  Land
                                </label>
                                <input
                                  id="assign-s2-country"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.country}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, country: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-nat" className={labelClass}>
                                  Nationalität
                                </label>
                                <input
                                  id="assign-s2-nat"
                                  type="text"
                                  className={inputClass}
                                  value={assignSecondForm.nationality}
                                  onChange={(e) =>
                                    setAssignSecondForm((f) => ({ ...f, nationality: e.target.value }))
                                  }
                                  disabled={assignSaving}
                                />
                              </div>
                              <div>
                                <label htmlFor="assign-s2-swiss" className={labelClass}>
                                  Schweizer/in
                                </label>
                                <select
                                  id="assign-s2-swiss"
                                  className={inputClass}
                                  style={{ cursor: assignSaving ? "default" : "pointer" }}
                                  value={
                                    assignSecondForm.isSwiss === null
                                      ? ""
                                      : assignSecondForm.isSwiss === true
                                        ? "true"
                                        : "false"
                                  }
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const v = raw === "" ? null : raw === "true";
                                    setAssignSecondForm((f) => {
                                      const next = { ...f, isSwiss: v };
                                      if (v === true) next.residencePermit = "";
                                      return next;
                                    });
                                  }}
                                  disabled={assignSaving}
                                >
                                  <option value="">Unbekannt</option>
                                  <option value="true">Ja</option>
                                  <option value="false">Nein</option>
                                </select>
                              </div>
                              {assignSecondForm.isSwiss !== true ? (
                                <div>
                                  <label htmlFor="assign-s2-permit" className={labelClass}>
                                    Aufenthaltsbewilligung
                                  </label>
                                  <select
                                    id="assign-s2-permit"
                                    className={inputClass}
                                    style={{ cursor: assignSaving ? "default" : "pointer" }}
                                    value={assignSecondForm.residencePermit}
                                    onChange={(e) =>
                                      setAssignSecondForm((f) => ({
                                        ...f,
                                        residencePermit: e.target.value,
                                      }))
                                    }
                                    disabled={assignSaving}
                                  >
                                    <option value="">—</option>
                                    <option value="B">B</option>
                                    <option value="C">C</option>
                                    <option value="L">L</option>
                                    <option value="G">G</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </div>
                              ) : null}
                              </React.Fragment>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>Einnahmen / Monat</label>
                        <div className={inputClass}>
                          {formatChfRent(monthlyEquivalentFromRevenueRows(assignRevenueRows))}
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Status (abgeleitet)</label>
                        <div className={inputClass}>
                          {tenancyDisplayStatusLabelDe(
                            deriveTenancyLifecyclePreviewForAssign(
                              assignMoveIn,
                              assignTerminationEffective,
                              assignActualMoveOut
                            )
                          )}
                        </div>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="mb-2 text-[9px] font-bold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
                          Einnahmen
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
                          <div>
                            <label htmlFor="assign-rev-type" className={labelClass}>
                              Typ
                            </label>
                            <RevenueTypeSelect
                              id="assign-rev-type"
                              selectClassName={inputClass}
                              value={assignRevenueForm.type}
                              frequency={assignRevenueForm.frequency}
                              onChange={(e) => setAssignRevenueForm((f) => ({ ...f, type: e.target.value }))}
                              disabled={assignSaving}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Betrag (CHF)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              className={inputClass}
                              value={assignRevenueForm.amount_chf}
                              onChange={(e) => {
                                setAssignRevenueForm((f) => ({ ...f, amount_chf: e.target.value }));
                              }}
                              disabled={assignSaving}
                            />
                          </div>
                          <div>
                            <label className={labelClass}>Frequenz</label>
                            <select
                              className={inputClass}
                              style={{ cursor: assignSaving ? "default" : "pointer" }}
                              value={assignRevenueForm.frequency}
                              onChange={(e) => {
                                const nextFreq = e.target.value;
                                setAssignRevenueForm((f) => {
                                  const updated = applyRecurringRevenueDatesFromTenancy(
                                    f,
                                    nextFreq,
                                    assignMoveIn,
                                    tenancyDraftDisplayEndIso(assignActualMoveOut, assignTerminationEffective)
                                  );
                                  // Setup-Fee intake safety: one_time forces setup_fee so a
                                  // stale legacy type can never be added to a new tenancy.
                                  return {
                                    ...updated,
                                    type: coerceRevenueTypeForFrequency(updated.type, nextFreq),
                                  };
                                });
                              }}
                              disabled={assignSaving}
                            >
                              <option value="monthly">Monatlich</option>
                              <option value="yearly">Jährlich</option>
                              <option value="one_time">Einmalig</option>
                            </select>
                          </div>
                          <div>
                            <label className={labelClass}>Start (optional)</label>
                            <input
                              type="date"
                              className={inputClass}
                              value={assignRevenueForm.start_date}
                              onChange={(e) => setAssignRevenueForm((f) => ({ ...f, start_date: e.target.value }))}
                              disabled={assignSaving}
                            />
                          </div>
                          {normalizeRevenueFrequency(assignRevenueForm.frequency) === "one_time" ? (
                            <div>
                              <label className={labelClass}>Ende (optional)</label>
                              <input
                                type="date"
                                className={inputClass}
                                value={assignRevenueForm.end_date}
                                onChange={(e) => setAssignRevenueForm((f) => ({ ...f, end_date: e.target.value }))}
                                disabled={assignSaving}
                              />
                            </div>
                          ) : (
                            <div>
                              <label className={labelClass}>Ende (Mietende)</label>
                              <div className={`${inputClass} font-semibold`}>
                                {formatDateOnly(
                                  tenancyDraftDisplayEndIso(assignActualMoveOut, assignTerminationEffective) || ""
                                )}
                              </div>
                              <div className="mt-1 max-w-[220px] text-[10px] text-[#64748b] dark:text-[#6b7a9a]">
                                Ende wird automatisch aus Mietende übernommen.
                              </div>
                            </div>
                          )}
                          <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
                            <label className={labelClass}>Notizen (optional)</label>
                            <input
                              type="text"
                              className={inputClass}
                              value={assignRevenueForm.notes}
                              onChange={(e) => setAssignRevenueForm((f) => ({ ...f, notes: e.target.value }))}
                              disabled={assignSaving}
                            />
                          </div>
                          <button
                            type="button"
                            disabled={assignSaving}
                            onClick={() => {
                              const id = `ar-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                              const freq = normalizeRevenueFrequency(assignRevenueForm.frequency);
                              const mi = dateOnlyOrNull(assignMoveIn) || "";
                              let sd = assignRevenueForm.start_date;
                              let ed = assignRevenueForm.end_date;
                              if (freq === "monthly" || freq === "yearly") {
                                if (!dateOnlyOrNull(sd)) sd = mi;
                                ed = "";
                              }
                              const row = {
                                id,
                                type: String(assignRevenueForm.type || "").trim(),
                                amount_chf: assignRevenueForm.amount_chf,
                                frequency: freq,
                                start_date: sd,
                                end_date: ed,
                                notes: assignRevenueForm.notes,
                              };
                              setAssignRevenueRows((prev) => [...(Array.isArray(prev) ? prev : []), row]);
                              setAssignRevenueForm((f) => ({ ...f, amount_chf: "", notes: "" }));
                            }}
                            className={`rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-xs font-bold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.05] ${
                              assignSaving ? "cursor-default opacity-50" : "cursor-pointer"
                            }`}
                          >
                            + Einnahme hinzufügen
                          </button>
                        </div>

                        <div style={{ marginTop: "10px", overflowX: "auto" }}>
                          <table className={tableClass}>
                            <thead>
                              <tr>
                                <th className={thCellClass}>Typ</th>
                                <th className={`${thCellClass} text-right`}>Betrag</th>
                                <th className={thCellClass}>Frequenz</th>
                                <th className={thCellClass}>Aktion</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(Array.isArray(assignRevenueRows) ? assignRevenueRows : []).map((rr) => (
                                <tr key={rr.id}>
                                  <td className={tdCellClass}>{revenueTypeLabelForDisplay(rr.type)}</td>
                                  <td className={`${tdCellClass} text-right font-bold text-[#0f172a] dark:text-[#eef2ff]`}>
                                    {formatChfRent(parseRevenueAmount(rr.amount_chf) ?? rr.amount_chf)}
                                  </td>
                                  <td className={tdCellClass}>{revenueFrequencyLabel(rr.frequency)}</td>
                                  <td className={tdCellClass}>
                                    <button
                                      type="button"
                                      disabled={assignSaving}
                                      onClick={() =>
                                        setAssignRevenueRows((prev) => (prev || []).filter((x) => x.id !== rr.id))
                                      }
                                      className="rounded-[8px] border border-red-500/20 bg-red-500/10 px-2.5 py-1 text-xs font-bold text-[#f87171] hover:bg-red-500/15 disabled:cursor-default"
                                      style={{ cursor: assignSaving ? "default" : "pointer" }}
                                    >
                                      Löschen
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="assign-tenant-deposit-type" className={labelClass}>
                          Kautionsart Mieter
                        </label>
                        <select
                          id="assign-tenant-deposit-type"
                          className={inputClass}
                          style={{ cursor: assignSaving ? "default" : "pointer" }}
                          value={assignTenantDepositType}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAssignTenantDepositType(v);
                            if (v !== "insurance") setAssignTenantDepositProvider("");
                          }}
                          disabled={assignSaving}
                        >
                          <option value="">—</option>
                          <option value="bank">Bank</option>
                          <option value="insurance">Versicherung</option>
                          <option value="cash">Bar</option>
                          <option value="none">Keine</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="assign-tenant-deposit-amount" className={labelClass}>
                          Kautionsbetrag Mieter (CHF)
                        </label>
                        <input
                          id="assign-tenant-deposit-amount"
                          type="number"
                          min="0"
                          step="0.01"
                          className={inputClass}
                          value={assignTenantDepositAmount}
                          onChange={(e) => setAssignTenantDepositAmount(e.target.value)}
                          disabled={assignSaving}
                        />
                      </div>
                      {assignTenantDepositType === "insurance" ? (
                        <div>
                          <label htmlFor="assign-tenant-deposit-provider" className={labelClass}>
                            Anbieter
                          </label>
                          <select
                            id="assign-tenant-deposit-provider"
                            className={inputClass}
                            style={{ cursor: assignSaving ? "default" : "pointer" }}
                            value={assignTenantDepositProvider}
                            onChange={(e) => setAssignTenantDepositProvider(e.target.value)}
                            disabled={assignSaving}
                          >
                            <option value="">—</option>
                            <option value="swisscaution">SwissCaution</option>
                            <option value="smartcaution">SmartCaution</option>
                            <option value="firstcaution">FirstCaution</option>
                            <option value="gocaution">GoCaution</option>
                            <option value="other">Sonstige</option>
                          </select>
                        </div>
                      ) : null}
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
                      <button
                        type="submit"
                        disabled={assignSaving}
                        className={`rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3.5 py-2 text-sm font-semibold text-white ${
                          assignSaving ? "cursor-default opacity-50" : "cursor-pointer"
                        }`}
                        style={{ border: "none", fontWeight: 700 }}
                      >
                        {assignSaving ? "Speichern …" : "Speichern"}
                      </button>
                      <button
                        type="button"
                        disabled={assignSaving}
                        onClick={() => {
                          setAssignOpen(false);
                          resetAssignForm();
                        }}
                        className="rounded-[8px] border border-black/10 bg-transparent px-3.5 py-2 text-sm font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-default"
                        style={{ cursor: assignSaving ? "default" : "pointer" }}
                      >
                        Abbrechen
                      </button>
                    </div>
                  </form>
  );
}
